import React, { useState, useRef } from "react";
import { Volume2, Pause, Play, Square, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";

// Generates a role-scoped summary first, then speaks it via Web Speech API (with transcript fallback).
export default function ReadAloud({ context, testId = "read-aloud" }) {
  const { user, lang, t } = useApp();
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState("idle"); // idle | playing | paused
  const [summary, setSummary] = useState("");
  const [rate, setRate] = useState(1);
  const [showText, setShowText] = useState(false);
  const uttRef = useRef(null);

  const speak = (text) => {
    if (!("speechSynthesis" in window)) { setShowText(true); return; }
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = lang === "ar" ? "ar-SA" : "en-US";
    u.rate = rate;
    u.onend = () => setState("idle");
    uttRef.current = u;
    window.speechSynthesis.speak(u);
    setState("playing");
  };

  const start = async () => {
    setLoading(true);
    try {
      const role = user?.role === "PLATFORM_ADMIN" ? "Executive" : (user?.role || "Executive");
      const { data } = await api.post("/summarize", { context: String(context).slice(0, 4000), role, lang });
      setSummary(data.summary);
      speak(data.summary);
    } catch {
      const fb = String(context).slice(0, 500);
      setSummary(fb); speak(fb);
    } finally { setLoading(false); }
  };

  const pause = () => { window.speechSynthesis.pause(); setState("paused"); };
  const resume = () => { window.speechSynthesis.resume(); setState("playing"); };
  const stop = () => { window.speechSynthesis.cancel(); setState("idle"); };

  return (
    <div className="inline-flex flex-wrap items-center gap-2" data-testid={`${testId}-wrap`}>
      {state === "idle" && (
        <button data-testid={`${testId}-btn`} onClick={start} disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-xl border border-accent/50 bg-accent/10 px-3 py-1.5 text-xs font-semibold text-accent hover:bg-accent/20 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Volume2 className="h-4 w-4" />} {t("readAloud")}
        </button>
      )}
      {state !== "idle" && (
        <>
          {state === "playing"
            ? <button data-testid={`${testId}-pause`} onClick={pause} className="rounded-lg border border-border p-1.5 hover:bg-secondary"><Pause className="h-4 w-4" /></button>
            : <button data-testid={`${testId}-resume`} onClick={resume} className="rounded-lg border border-border p-1.5 hover:bg-secondary"><Play className="h-4 w-4" /></button>}
          <button data-testid={`${testId}-stop`} onClick={stop} className="rounded-lg border border-border p-1.5 hover:bg-secondary"><Square className="h-4 w-4" /></button>
          <select data-testid={`${testId}-speed`} value={rate} aria-label={t("speed")}
            onChange={(e) => { setRate(parseFloat(e.target.value)); if (summary) speak(summary); }}
            className="rounded-lg border border-border bg-background px-2 py-1 text-xs">
            {[0.75, 1, 1.25, 1.5, 2].map((r) => <option key={r} value={r}>{r}x</option>)}
          </select>
        </>
      )}
      {summary && (
        <button onClick={() => setShowText((s) => !s)} className="text-xs text-muted-foreground underline">{t("transcript")}</button>
      )}
      {showText && summary && (
        <p data-testid={`${testId}-text`} className="mt-1 w-full rounded-lg border border-border bg-secondary/50 p-2 text-xs text-muted-foreground">{summary}</p>
      )}
    </div>
  );
}
